import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SessionExpiryService } from '../services/session-expiry.service';

const AUTH0_ERROR_CODES = new Set([
  'login_required',
  'consent_required',
  'missing_refresh_token',
  'invalid_grant'
]);

@Injectable()
export class SessionExpiryInterceptor implements HttpInterceptor {
  private readonly normalizedApiUrl: string;

  constructor(private sessionExpiryService: SessionExpiryService) {
    this.normalizedApiUrl = this.normalizeUrl(environment.apiUrl);
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isApiRequest(req.url)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (!this.shouldTriggerReauthentication(error)) {
          return throwError(() => error);
        }

        this.sessionExpiryService.triggerReauthentication();
        return throwError(() => error);
      })
    );
  }

  private shouldTriggerReauthentication(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        return true;
      }

      return this.isKnownAuth0Error(error.error);
    }

    return this.isKnownAuth0Error(error);
  }

  private isKnownAuth0Error(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const rawValue = (error as Record<string, unknown>)['error'];
    return typeof rawValue === 'string' && AUTH0_ERROR_CODES.has(rawValue);
  }

  private isApiRequest(url: string): boolean {
    const normalizedUrl = this.normalizeUrl(url);
    return normalizedUrl.startsWith(this.normalizedApiUrl);
  }

  private normalizeUrl(value: string): string {
    return value.endsWith('/') ? value.slice(0, -1) : value;
  }
}
